import { t } from '@trimble/i18n';
import {
  BtnClasses,
  CustomInput,
  CustomTooltip,
  IconButton,
  Spinner,
} from '@tcweb/connect-ui';
import {
  createEffect,
  createMemo,
  createSignal,
  For,
  JSX,
  Match,
  Show,
  Switch,
} from 'solid-js';
import { useDropdown } from '@trimble/solidjs-utils';
import { Project, Region } from '../types/project-select.types';
import { debounce } from '@solid-primitives/scheduled';
import {
  RESULT_LIMIT,
  getRegionalBaseUrl,
} from '@trimble/connect-project-providers';
import { useQuery, UseQueryResult } from '@tanstack/solid-query';
import {
  listAllSearchResults,
  ListAllSearchResultsData,
  listAllSearchResultsQueryKey,
  Options,
} from '@data-access/connect/tc-tcps/generated';

const SEARCH_DEBOUNCE_MS = 300;

interface ProjectSearchProps {
  region: Region;

  onProjectSelect(region: Region, project: Project): void;
  onCloseClick?(): void;
}

export const ProjectSearch = (props: ProjectSearchProps): JSX.Element => {
  const { isOpen, toggle, ref } = useDropdown();
  const [filterText, setFilterText] = createSignal<string>('');

  const setFilterTextDebounced = debounce((value: string) => {
    setFilterText(value);
  }, SEARCH_DEBOUNCE_MS);

  const showDropdown = createMemo(() => filterText().length > 0);

  const handleSearchInputChange = (
    e: InputEvent & { currentTarget: HTMLInputElement }
  ): void => {
    setFilterTextDebounced(e.currentTarget.value);
  };

  const getFilteredProjectsQuery = (): UseQueryResult<Project[]> => {
    const region = props.region;
    const text = filterText().trim();
    const options: Options<ListAllSearchResultsData> = {
      query: {
        query: text,
        sort: '+name',
        type: 'PROJECT',
      },
      baseUrl: getRegionalBaseUrl(region),
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Range: `items=0-${String(RESULT_LIMIT - 1)}`,
      },
    };

    return useQuery(() => ({
      queryKey: listAllSearchResultsQueryKey(options),
      enabled: text.length > 0,
      queryFn: async ({ signal }) => {
        const { data } = await listAllSearchResults({
          ...options,
          signal,
          throwOnError: true,
        });
        return data;
      },
      select: (d): Project[] => {
        return d.map((item) => ({
          id: item.details.id,
          name: item.details.name ?? item.details.id,
        }));
      },
    }));
  };

  const filteredProjectsQuery = createMemo(() => getFilteredProjectsQuery());

  createEffect(() => {
    const show = showDropdown();
    if (show && !isOpen()) {
      toggle();
    }
    if (!show && isOpen()) {
      toggle();
    }
  });

  return (
    <div
      data-testid="project-search"
      class="relative w-full text-base-content"
      ref={ref}
    >
      <div class="flex w-full items-center justify-between relative">
        <CustomInput
          label=""
          name="project-search-input"
          class="flex-1 [&_input]:!px-10 [&_input]:!text-base-content [&_input]:bg-base-page [&_input]:border [&_input]:!border-base-100 [&_input:focus]:bg-base-page"
          placeholder={t('general.search')}
          autocomplete={false}
          onInputRef={(ref) => {
            ref?.focus();
          }}
          onInput={handleSearchInputChange}
        />
        <i class="absolute left-2 icon-font tc-icon-search text-base-content" />
        <div class="absolute right-0">
          <IconButton
            color={BtnClasses.TERTIARY}
            title={t('general.close')}
            icon="tc-icon-content-close"
            size="icon-medium"
            clicked={props.onCloseClick}
          />
        </div>
      </div>
      <Show when={showDropdown()}>
        <div
          data-testid="project-search-results"
          class="w-full max-h-[200px] dropdown-pane dropdown is-open overflow-y-auto bg-base-page text-base-content"
        >
          <Switch>
            <Match when={filteredProjectsQuery().isLoading}>
              <div data-testid="project-search-spinner" class="flex-1">
                <Spinner class="min-h-[10rem]" show={true} />
              </div>
            </Match>
            <Match when={filteredProjectsQuery().isError}>
              <div
                data-testid="project-search-error"
                class="px-4 text-base-content"
              >
                {t('shared.ui.projectSelect.failedToSearchProjects')}
              </div>
            </Match>
            <Match when={filteredProjectsQuery().isSuccess}>
              <Show
                when={
                  filteredProjectsQuery().data?.length === 0 ||
                  filteredProjectsQuery().data?.length === undefined
                }
              >
                <div
                  data-testid="project-search-no-results"
                  class="px-4 text-base-content"
                >
                  {t('shared.ui.projectSelect.noResultsFound')}
                </div>
              </Show>
              <Show when={filteredProjectsQuery().data!.length > 0}>
                <For each={filteredProjectsQuery().data!}>
                  {(project) => (
                    <div
                      data-testid="project-search-result"
                      class="flex h-10 px-4 items-center cursor-pointer hover:bg-gray-light"
                      onClick={() => {
                        props.onProjectSelect(props.region, project);
                      }}
                    >
                      <CustomTooltip
                        position="bottom"
                        class="flex-1 truncate"
                        tooltipClass="opacity-90 !max-w-fit"
                        text={project.name}
                      >
                        {project.name}
                      </CustomTooltip>
                    </div>
                  )}
                </For>
              </Show>
            </Match>
          </Switch>
        </div>
      </Show>
    </div>
  );
};

export default ProjectSearch;
